import { useState, useEffect } from 'react'
import { useAuthStore } from '../stores/authStore'
import { preferencesDb } from '../lib/supabase'
import { synapseAPI } from '../lib/synapse-api'

interface SpecFile {
  filename: string
  name: string
  size: number
  modified: string
  download_url: string
}

export function SettingsPage() {
  const { user, profile } = useAuthStore()
  const [snippet, setSnippet] = useState('')
  const [sending, setSending] = useState(false)
  const [sent, setSent] = useState(false)
  const [savedSnippets, setSavedSnippets] = useState<string[]>([])
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadResult, setUploadResult] = useState<string>('')
  const [specs, setSpecs] = useState<SpecFile[]>([])
  const [specsLoading, setSpecsLoading] = useState(true)

  // Load saved snippets from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('claude_snippets')
    if (saved) {
      try {
        setSavedSnippets(JSON.parse(saved))
      } catch {}
    }
  }, [])

  // Load spec files
  useEffect(() => {
    const loadSpecs = async () => {
      try {
        const response = await fetch('/api/claude-logs/api/specs')
        if (response.ok) {
          const data = await response.json()
          setSpecs(data)
        }
      } catch (err) {
        console.error('Failed to load specs:', err)
      } finally {
        setSpecsLoading(false)
      }
    }
    loadSpecs()
  }, [])

  const saveSnippet = () => {
    if (!snippet.trim()) return
    const updated = [snippet, ...savedSnippets].slice(0, 10) // Keep last 10
    setSavedSnippets(updated)
    localStorage.setItem('claude_snippets', JSON.stringify(updated))
  }

  const sendToClaude = async () => {
    if (!snippet.trim()) return
    setSending(true)

    try {
      // Save to snippets history
      saveSnippet()

      // Send via voice task API (creates a task Claude will see)
      await synapseAPI.submitVoiceTask(`[SNIPPET FROM JOEY] ${snippet}`, 'high')

      setSent(true)
      setSnippet('')
      setTimeout(() => setSent(false), 3000)
    } catch (err) {
      console.error('Failed to send snippet:', err)
    } finally {
      setSending(false)
    }
  }

  const copySnippet = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const downloadSpec = async (filename: string) => {
    try {
      const response = await fetch(`/api/claude-logs/api/specs/${filename}`)
      if (!response.ok) throw new Error('Download failed')

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to download spec:', err)
      alert('Failed to download spec file')
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      setUploadResult('')
    }
  }

  const uploadFile = async () => {
    if (!selectedFile) return
    setUploading(true)
    setUploadResult('')

    try {
      const reader = new FileReader()

      reader.onload = async (e) => {
        const content = e.target?.result as string
        const base64Content = content.split(',')[1] // Remove data:type;base64, prefix

        const result = await synapseAPI.uploadFile(
          selectedFile.name,
          '/home/forge/mothershipio.ca/shared/uploads/',
          base64Content,
          'base64'
        )

        if (result.success) {
          setUploadResult(`✓ Uploaded: ${result.file_path}`)
          setSelectedFile(null)
          // Auto-clear after 5 seconds
          setTimeout(() => setUploadResult(''), 5000)
        } else {
          setUploadResult(`✗ Error: ${result.error}`)
        }
      }

      reader.readAsDataURL(selectedFile)
    } catch (err) {
      setUploadResult(`✗ Failed: ${err}`)
    } finally {
      setUploading(false)
    }
  }

  return (
    <main className="flex-1 p-6 overflow-y-auto">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Settings</h1>
            <p className="text-gray-400 mt-1">Configure your dashboard and send snippets to Claude</p>
          </div>
          <a
            href="/dashboard/vault"
            className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg flex items-center gap-2 transition"
          >
            <span>🗄️</span>
            <span>Go to Vault</span>
          </a>
        </div>

        {/* Snippet Box - Main Feature */}
        <div className="bg-[#161b22] rounded-xl border border-[#30363d] overflow-hidden">
          <div className="px-6 py-4 border-b border-[#30363d] flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">📬</span>
              <div>
                <h2 className="text-lg font-semibold text-white">Send to Claude</h2>
                <p className="text-sm text-gray-400">Paste anything - code, notes, links, context</p>
              </div>
            </div>
            <a
              href="/dashboard/vault"
              className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center gap-1"
            >
              <span>View Vault →</span>
            </a>
          </div>

          <div className="p-6 space-y-4">
            <textarea
              value={snippet}
              onChange={(e) => setSnippet(e.target.value)}
              placeholder="Paste code, URLs, notes, or anything you want to share with Claude..."
              className="w-full h-48 px-4 py-3 bg-[#0d1117] border border-[#30363d] rounded-lg text-white placeholder-gray-500 font-mono text-sm resize-none focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
            />

            {/* File Upload Section */}
            <div className="border-t border-[#30363d] pt-4">
              <div className="flex items-center gap-4">
                <label className="flex-1">
                  <input
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <div className="flex items-center gap-3 px-4 py-3 bg-[#0d1117] border border-[#30363d] rounded-lg cursor-pointer hover:border-cyan-500 transition">
                    <span className="text-xl">📎</span>
                    <span className="text-sm text-gray-400">
                      {selectedFile ? selectedFile.name : 'Choose file to upload to Claude...'}
                    </span>
                  </div>
                </label>
                {selectedFile && (
                  <button
                    onClick={uploadFile}
                    disabled={uploading}
                    className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition disabled:opacity-50 flex items-center gap-2"
                  >
                    {uploading ? (
                      <>
                        <span className="animate-spin">⏳</span>
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <span>⬆️</span>
                        <span>Upload</span>
                      </>
                    )}
                  </button>
                )}
              </div>
              {uploadResult && (
                <div className={`mt-2 text-sm px-3 py-2 rounded ${
                  uploadResult.startsWith('✓')
                    ? 'bg-green-600/20 text-green-400'
                    : 'bg-red-600/20 text-red-400'
                }`}>
                  {uploadResult}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                {snippet.length > 0 && `${snippet.length} characters`}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={saveSnippet}
                  disabled={!snippet.trim()}
                  className="px-4 py-2 bg-[#21262d] hover:bg-[#30363d] text-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                  Save Draft
                </button>
                <button
                  onClick={sendToClaude}
                  disabled={!snippet.trim() || sending}
                  className={`px-6 py-2 rounded-lg font-medium transition flex items-center gap-2 ${
                    sent
                      ? 'bg-green-600 text-white'
                      : 'bg-cyan-600 hover:bg-cyan-700 text-white disabled:opacity-50 disabled:cursor-not-allowed'
                  }`}
                >
                  {sending ? (
                    <>
                      <span className="animate-spin">⏳</span>
                      <span>Sending...</span>
                    </>
                  ) : sent ? (
                    <>
                      <span>✓</span>
                      <span>Sent!</span>
                    </>
                  ) : (
                    <>
                      <span>📤</span>
                      <span>Send to Claude</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Saved Snippets */}
        {savedSnippets.length > 0 && (
          <div className="bg-[#161b22] rounded-xl border border-[#30363d] overflow-hidden">
            <div className="px-6 py-4 border-b border-[#30363d]">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <span>📋</span>
                <span>Recent Snippets</span>
                <span className="text-sm text-gray-500 font-normal ml-2">({savedSnippets.length})</span>
              </h2>
            </div>
            <div className="divide-y divide-[#30363d] max-h-64 overflow-y-auto">
              {savedSnippets.map((s, i) => (
                <div key={i} className="px-6 py-3 flex items-start gap-4 hover:bg-[#21262d] group">
                  <pre className="flex-1 text-sm text-gray-300 font-mono truncate">
                    {s.slice(0, 100)}{s.length > 100 ? '...' : ''}
                  </pre>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition">
                    <button
                      onClick={() => setSnippet(s)}
                      className="px-2 py-1 text-xs bg-[#30363d] hover:bg-[#3d444d] text-gray-300 rounded"
                    >
                      Load
                    </button>
                    <button
                      onClick={() => copySnippet(s)}
                      className="px-2 py-1 text-xs bg-[#30363d] hover:bg-[#3d444d] text-gray-300 rounded"
                    >
                      Copy
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Links */}
        <div className="bg-[#161b22] rounded-xl border border-[#30363d] overflow-hidden">
          <div className="px-6 py-4 border-b border-[#30363d]">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <span>🔗</span>
              <span>Quick Links</span>
            </h2>
          </div>
          <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
            <a href="/dashboard/vault" className="p-4 bg-[#0d1117] rounded-lg border border-[#30363d] hover:border-cyan-500 transition text-center">
              <div className="text-2xl mb-2">🗄️</div>
              <div className="text-sm text-gray-300">Code Vault</div>
            </a>
            <a href="/dashboard/claude" className="p-4 bg-[#0d1117] rounded-lg border border-[#30363d] hover:border-purple-500 transition text-center">
              <div className="text-2xl mb-2">🤖</div>
              <div className="text-sm text-gray-300">Claude Chat</div>
            </a>
            <a href="/dashboard/lightsite" className="p-4 bg-[#0d1117] rounded-lg border border-[#30363d] hover:border-green-500 transition text-center">
              <div className="text-2xl mb-2">⚡</div>
              <div className="text-sm text-gray-300">LightSite</div>
            </a>
            <a href="/dashboard/projects" className="p-4 bg-[#0d1117] rounded-lg border border-[#30363d] hover:border-yellow-500 transition text-center">
              <div className="text-2xl mb-2">📁</div>
              <div className="text-sm text-gray-300">Projects</div>
            </a>
          </div>
        </div>

        {/* Spec Downloads */}
        <div className="bg-[#161b22] rounded-xl border border-[#30363d] overflow-hidden">
          <div className="px-6 py-4 border-b border-[#30363d]">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <span>📄</span>
              <span>Specification Documents</span>
              <span className="text-sm text-gray-500 font-normal ml-2">({specs.length})</span>
            </h2>
            <p className="text-sm text-gray-400 mt-1">Download project specs and documentation</p>
          </div>
          <div className="p-6">
            {specsLoading ? (
              <div className="text-center text-gray-400 py-8">
                <span className="animate-spin inline-block">⏳</span>
                <span className="ml-2">Loading specs...</span>
              </div>
            ) : specs.length === 0 ? (
              <div className="text-center text-gray-400 py-8">
                No specification documents available
              </div>
            ) : (
              <div className="space-y-3">
                {specs.map((spec) => (
                  <div
                    key={spec.filename}
                    className="flex items-center justify-between p-4 bg-[#0d1117] border border-[#30363d] rounded-lg hover:border-cyan-500 transition group"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">📋</span>
                        <div>
                          <h3 className="text-white font-medium capitalize">{spec.name}</h3>
                          <div className="flex gap-3 text-sm text-gray-400 mt-1">
                            <span>{formatFileSize(spec.size)}</span>
                            <span>•</span>
                            <span>Modified {formatDate(spec.modified)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => downloadSpec(spec.filename)}
                      className="px-4 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-medium transition flex items-center gap-2 opacity-0 group-hover:opacity-100"
                    >
                      <span>⬇️</span>
                      <span>Download</span>
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* User Info */}
        <div className="bg-[#161b22] rounded-xl border border-[#30363d] overflow-hidden">
          <div className="px-6 py-4 border-b border-[#30363d]">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <span>👤</span>
              <span>Account</span>
            </h2>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-cyan-500 to-purple-600 flex items-center justify-center text-2xl font-bold text-white">
                {profile?.full_name?.[0] || user?.email?.[0]?.toUpperCase() || '?'}
              </div>
              <div>
                <div className="text-lg font-medium text-white">{profile?.full_name || 'User'}</div>
                <div className="text-gray-400">{user?.email}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}
